import React from 'react';
import { Link } from 'react-router-dom';
import DemoChat from '../components/series/DemoChat';
import { 
  MessageSquare, 
  Mail, 
  Clock, 
  Shield, 
  Brain, 
  Users, 
  BookOpen,
  Sparkles,
  ArrowRight,
  CheckCircle,
  Lock,
  Globe,
  TrendingUp,
  Heart,
  HelpCircle
} from 'lucide-react';

const FeatureCard = ({ icon: Icon, title, description, color = "red" }) => (
  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
    <div className={`w-12 h-12 rounded-xl bg-${color}-50 flex items-center justify-center mb-4`}>
      <Icon className={`w-6 h-6 text-${color}-500`} />
    </div>
    <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
  </div>
);

const ChallengeItem = ({ text }) => (
  <div className="flex items-start gap-3 p-4 bg-white/60 backdrop-blur rounded-xl border border-red-100">
    <div className="w-2 h-2 rounded-full bg-red-500 mt-2 flex-shrink-0" />
    <p className="text-gray-700">{text}</p>
  </div>
);

const PrivacyPoint = ({ icon: Icon, title, description }) => (
  <div className="flex items-start gap-4">
    <div className="w-10 h-10 rounded-lg bg-green-900/30 flex items-center justify-center flex-shrink-0">
      <Icon className="w-5 h-5 text-green-400" />
    </div>
    <div>
      <h4 className="font-semibold text-white mb-1">{title}</h4>
      <p className="text-gray-300 text-sm">{description}</p>
    </div>
  </div>
);

const CompanionPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/it-leadership" className="flex items-center gap-2 text-gray-900 hover:text-red-600 transition-colors">
            <ArrowRight className="w-4 h-4 rotate-180" />
            <span className="text-sm font-medium">Back to 30 Day Skills</span>
          </Link>
          <Link 
            to="/it-leadership#pricing" 
            className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
          >
            Get Started
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-red-50 via-white to-amber-50" />
        <div className="relative max-w-6xl mx-auto px-4 py-20 md:py-28">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-full text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              Your Always-Available Leadership Support
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              The Managerial
              <span className="text-red-600"> Companion</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 leading-relaxed">
              Think better. Lead confidently. <span className="font-semibold text-gray-900">Never alone.</span>
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/it-leadership#pricing"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-all shadow-lg shadow-red-500/25 hover:shadow-xl"
              >
                Get Started Today
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link 
                to="/login"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-all border border-gray-200"
              >
                Sign In to Dashboard
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Try It Now - Demo Chat */}
      <DemoChat />

      {/* Use it like this - Immediate Relief Section */}
      <section className="py-12 md:py-16 bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              Use it like this
            </h2>
            <p className="text-gray-600">When you need help this week, not next month.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="flex items-start gap-3 p-4 bg-gradient-to-r from-red-50 to-orange-50 rounded-xl border border-red-100">
              <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <MessageSquare className="w-4 h-4 text-red-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">&quot;I have a difficult conversation tomorrow&quot;</p>
                <p className="text-sm text-gray-600 mt-1">Think it through before walking in</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl border border-amber-100">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Users className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">&quot;Unsure how to handle this team issue&quot;</p>
                <p className="text-sm text-gray-600 mt-1">Work through it safely, no judgment</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-100">
              <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Brain className="w-4 h-4 text-purple-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">&quot;Need to think this through&quot;</p>
                <p className="text-sm text-gray-600 mt-1">Your private sounding board, anytime</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-100">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Shield className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">&quot;Processing feedback I just received&quot;</p>
                <p className="text-sm text-gray-600 mt-1">Reflect without being watched</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* When You Need to Talk It Through */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-red-50 to-amber-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              When You Need to Talk It Through
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Leadership can feel isolating. These moments need a trusted sounding board.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            <ChallengeItem text="Difficult conversation approaching — need to think it through" />
            <ChallengeItem text="Conflicting priorities — which matters most?" />
            <ChallengeItem text="Team challenge at midnight — can't wait until tomorrow" />
            <ChallengeItem text="Decision uncertainty — need a sounding board" />
            <ChallengeItem text="Self-doubt creeping in — need perspective" />
            <ChallengeItem text="No one safe to discuss sensitive issues with" />
          </div>
        </div>
      </section>

      {/* The Companion You Need */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              The Companion You Need, When You Need It
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <FeatureCard 
              icon={Clock}
              title="Available 24/7"
              description="Via email and web interface whenever you need support — even at 2am before a tough meeting."
            />
            <FeatureCard 
              icon={BookOpen}
              title="Grounded in Principles"
              description="Based on the 30-Day Skills series frameworks, connecting advice to proven leadership concepts."
            />
            <FeatureCard 
              icon={Shield}
              title="Confidential Space"
              description="Work through challenges in complete privacy. Your reflections stay yours."
            />
          </div>
          <div className="bg-gray-50 rounded-2xl p-8 max-w-3xl mx-auto">
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Helps you <strong>think better</strong>, not just get answers</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Asks the <strong>questions you need</strong> to consider</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700"><strong>Non-judgmental support</strong> whenever you need it</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Communicate Your Way */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Communicate Your Way
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Email Option */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <div className="w-14 h-14 rounded-xl bg-blue-50 flex items-center justify-center mb-6">
                <Mail className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Email Option</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">Send questions to <strong>coach@30dayskills.co.uk</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">Receive thoughtful responses within minutes</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">Continue conversations via email thread</span>
                </li>
              </ul>
            </div>
            {/* Web Interface */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <div className="w-14 h-14 rounded-xl bg-purple-50 flex items-center justify-center mb-6">
                <Globe className="w-7 h-7 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Web Interface</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600"><strong>Unlimited</strong> real-time chat via dashboard</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">Start on email, continue on web — seamless</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">All conversation history accessible anytime</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Context That Builds Over Time */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Context That Builds Over Time
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              The more you reflect, the smarter and more personalized your support becomes.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            <FeatureCard 
              icon={Users}
              title="Team Tracking"
              description="Remembers your interactions with each team member, building a picture of your working relationships."
            />
            <FeatureCard 
              icon={Heart}
              title="Relationship Insights"
              description="Recalls your thoughts about people you manage and report to, providing contextual advice."
            />
            <FeatureCard 
              icon={TrendingUp}
              title="Leadership Journey"
              description="Builds contextual understanding over time, recognizing patterns and growth areas."
            />
          </div>
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 md:p-8 max-w-3xl mx-auto border border-green-100">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Privacy-First People Tracking</h4>
                <p className="text-gray-700 leading-relaxed">
                  Your team members identified for you using <strong>privacy-safe aliases you control</strong> — first name plus initial, role descriptions, or any identifier that works for you. <strong>Never full legal names.</strong>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Safe Space for Honest Reflection */}
      <section className="py-16 md:py-24 bg-gray-900 text-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Safe Space for Honest Leadership Reflection
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Think freely about sensitive people issues with ethical AI usage throughout.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <PrivacyPoint 
              icon={Lock}
              title="Completely Confidential"
              description="Your conversations remain private and are never shared with anyone."
            />
            <PrivacyPoint 
              icon={Users}
              title="Team Members Protected"
              description="Privacy-safe aliases only, never full names or identifying information."
            />
            <PrivacyPoint 
              icon={Shield}
              title="Secure Storage"
              description="Your leadership reflections safely stored with enterprise-grade security."
            />
            <PrivacyPoint 
              icon={CheckCircle}
              title="Never Shared or Used for Training"
              description="Your data is never shared with third parties or used to train AI models."
            />
          </div>
        </div>
      </section>

      {/* A Tool for Better Thinking */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              A Tool for Better Thinking
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard 
              icon={Clock}
              title="Midnight Colleague"
              description="The trusted colleague you can talk to at midnight — or any time you need to process a challenge."
            />
            <FeatureCard 
              icon={Heart}
              title="Non-Judgmental Mentor"
              description="Never judges your questions or concerns. Every leadership challenge is valid."
            />
            <FeatureCard 
              icon={Shield}
              title="Sensitive Sounding Board"
              description="For people issues you can't discuss elsewhere — performance, conflicts, frustrations."
            />
            <FeatureCard 
              icon={TrendingUp}
              title="Pattern Recognition"
              description="Tracks patterns in your relationships and challenges, surfacing insights you might miss."
            />
            <FeatureCard 
              icon={HelpCircle}
              title="Better Questions"
              description="Asks the questions you need to hear, helping you think through situations more deeply."
            />
            <FeatureCard 
              icon={Sparkles}
              title="Progress Tracking"
              description="Shows you how far you've come in your leadership journey over time."
            />
          </div>
        </div>
      </section>

      {/* Aligned With Your Learning */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Aligned With Your Learning Journey
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Supports your progression from foundation to strategic mastery, referencing relevant book concepts when helpful.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <BookOpen className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Framework Understanding</h4>
                    <p className="text-gray-600 text-sm">Knows the principles you&apos;re learning from the 30-Day series</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <Brain className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Concept Reinforcement</h4>
                    <p className="text-gray-600 text-sm">Helps apply ideas to your real situations</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Series Integration</h4>
                    <p className="text-gray-600 text-sm">Connects ideas across all 8 books in the series</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-amber-100">
              <h3 className="font-semibold text-gray-900 mb-4">Bring Your Real Leadership Challenges</h3>
              <div className="space-y-3">
                {[
                  '"My senior developer seems disengaged — help me think this through"',
                  '"Preparing for a difficult performance conversation"',
                  '"My manager and I aren\'t aligned — what am I missing?"',
                  '"Team conflict between J and S — where do I start?"',
                  '"Processing tough feedback I just received"',
                  '"Evaluating this decision\'s impact on team dynamics"'
                ].map((challenge, idx) => (
                  <div key={idx} className="flex items-start gap-3 text-sm">
                    <MessageSquare className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600 italic">{challenge}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Think Better. Lead Confidently.
          </h2>
          <p className="text-gray-400 mb-10 text-lg">
            Navigate leadership challenges with confidence. Never alone.
          </p>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            <div className="bg-gray-800 rounded-xl p-4">
              <Mail className="w-6 h-6 text-red-400 mx-auto mb-2" />
              <p className="text-white font-medium text-sm">Email Anytime</p>
              <p className="text-gray-500 text-xs">coach@30dayskills.co.uk</p>
            </div>
            <div className="bg-gray-800 rounded-xl p-4">
              <MessageSquare className="w-6 h-6 text-red-400 mx-auto mb-2" />
              <p className="text-white font-medium text-sm">Unlimited Web Chat</p>
              <p className="text-gray-500 text-xs">Via your dashboard</p>
            </div>
            <div className="bg-gray-800 rounded-xl p-4">
              <Shield className="w-6 h-6 text-red-400 mx-auto mb-2" />
              <p className="text-white font-medium text-sm">Privacy-Protected</p>
              <p className="text-gray-500 text-xs">Secure & confidential</p>
            </div>
            <div className="bg-gray-800 rounded-xl p-4">
              <Brain className="w-6 h-6 text-red-400 mx-auto mb-2" />
              <p className="text-white font-medium text-sm">Contextual Memory</p>
              <p className="text-gray-500 text-xs">Grows with you</p>
            </div>
          </div>

          <Link 
            to="/it-leadership#pricing"
            className="inline-flex items-center gap-2 px-8 py-4 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-all shadow-lg hover:shadow-xl text-lg"
          >
            Get Started Today
            <ArrowRight className="w-5 h-5" />
          </Link>
          
          <p className="text-gray-500 text-sm mt-6">
            Part of the 30-Day Skills ecosystem
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-950 py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} 30 Day Skills. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link to="/privacy" className="text-gray-500 hover:text-white text-sm transition-colors">
                Privacy Policy
              </Link>
              <Link to="/companion-terms" className="text-gray-500 hover:text-white text-sm transition-colors">
                AI Terms
              </Link>
              <Link to="/companion-privacy" className="text-gray-500 hover:text-white text-sm transition-colors">
                AI Privacy
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CompanionPage;
